let cleanupHandler = null;

/**
 * Attaches a one-time "click outside" listener to close the menu.
 * @param {HTMLElement} element - The menu element to monitor.
 * @param {Function} callback - Called when a click occurs outside the element.
 * @returns {Function} - A cleanup function to remove listeners manually.
 */
function onClickOutside(element, callback) {
    const handler = (event) => {
        if (!element.contains(event.target)) callback();
    };

    setTimeout(() => {
        document.addEventListener('click', handler, {
            capture: true,
            once: true,
        });
        document.addEventListener('contextmenu', handler, {
            capture: true,
            once: true,
        });
    });

    return () => {
        document.removeEventListener('click', handler, { capture: true });
        document.removeEventListener('contextmenu', handler, { capture: true });
    };
}

/**
 * Removes any existing context menu from the DOM and its event listeners.
 */
function closeContextMenu() {
    cleanupHandler?.();
    cleanupHandler = null;

    document.getElementById('custom-context-menu')?.remove();
}

/**
 * Creates a DOM element for the context menu and populates it with items.
 * @param {Array<{ label: string, action?: Function, disabled?: boolean }>} menuItems
 * @param {Function} onClose - Callback when menu is closed.
 * @returns {HTMLElement} - The constructed menu element.
 */
function createMenu(menuItems, onClose) {
    const menu = document.createElement('div');
    menu.id = 'custom-context-menu';
    menu.className = 'custom-context-menu';

    menuItems.forEach(({ label, action, disabled }) => {
        if (!label) return;

        const itemButton = document.createElement('button');
        itemButton.className = 'context-menu-item';
        itemButton.innerHTML = `<span>${label}</span>`;
        itemButton.disabled = Boolean(disabled);

        itemButton.addEventListener('click', (e) => {
            if (typeof action === 'function') action(e);
            onClose();
        });

        menu.appendChild(itemButton);
    });

    return menu;
}

/**
 * Positions the context menu relative to the triggering button.
 * @param {HTMLElement} menu - The menu element.
 * @param {HTMLElement} button - The button that triggered the menu.
 */
function positionMenu(menu, button) {
    menu.style.position = 'absolute';
    menu.style.visibility = 'hidden';

    const container = button.closest('.window-modal') || document.body;
    container.appendChild(menu);

    const menuWidth = menu.offsetWidth;
    const rect = button.getBoundingClientRect();

    menu.style.top = `${rect.bottom + 2}px`;
    menu.style.left = `${rect.right - menuWidth}px`;
    menu.style.visibility = 'visible';
}

/**
 * Creates and displays a custom context menu.
 * @param {Array<Object>} menuItems - Array of menu item objects.
 * @param {MouseEvent} event - The original click/contextmenu event.
 */
export function showContextMenu(menuItems, event) {
    if (!Array.isArray(menuItems) || menuItems.length === 0) return;

    closeContextMenu();
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const menu = createMenu(menuItems, closeContextMenu);
    positionMenu(menu, button);

    cleanupHandler = onClickOutside(menu, closeContextMenu);
}
